'use client';

import { requestLogin } from '@/lib/auth/login-dialog';

import { StudioAction } from '@/components/ui/StudioAction';

import studioStyles from './KagemaneStudio.module.css';

import { useKagemaneCatalog } from '@/lib/use-kagemane-catalog';
import { useKagemanePerformance } from '@/lib/use-kagemane-performance';
import { KagemaneCatalogNotice } from './KagemaneCatalogNotice';
import { KAGEMANE_LIBRARY_COLUMNS } from '@/lib/kagemane-media-layout';
import { KagemaneMasonry } from './KagemaneMasonry';
import { KagemaneReferenceCard } from './KagemaneReferenceCard';

import { ArrowLeft, Coins } from 'lucide-react';
import dynamic from 'next/dynamic';
import { useReducedMotion } from 'framer-motion';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocale, useTranslations } from 'next-intl';

import { KagemaneSetupFields } from './KagemaneSetupFields';
import { KagemaneMobileSetup } from './KagemaneMobileSetup';
import { KagemaneMediaPicker } from './KagemaneMediaPicker';
import { KagemaneReferenceEditor } from './KagemaneReferenceEditor';
import { KagemaneVideoDetail } from './KagemaneVideoDetail';
import { KagemaneGiftChoice } from './KagemaneGiftChoice';
import { KagemaneGenjutsuFields } from './KagemaneGenjutsuFields';
import { KagemaneGenerationCost, useKagemaneGenerationLabel } from './KagemaneGenerationCost';
import { useKagemaneQuote } from '@/lib/use-kagemane-quote';
import { GuideOrbLoading } from '@/components/ui/GuideOrb';
import { saveKagemaneLoginDraft, consumeKagemaneLoginDraft, kagemaneDraftStorage } from '@/lib/kagemane-login-draft';
import { submitKagemaneGeneration, type KagemaneSession } from '@/lib/kagemane-generation';
import type { ProjectTask } from '@/lib/api/tasks';
import { KAGEMANE_RESOLUTIONS_25, KAGEMANE_HEYGEN_RESOLUTIONS, type KagemaneResolution } from '@/components/ui/KagemaneResolutionChoice';
import type { UserMaterial } from '@/lib/api/materials';
import type { GenjutsuMode, GenjutsuQuote, KagemaneQuote, VideoReplicationModel, VideoReplicationReferenceRole } from '@/lib/api/video-replications';
import { buildLoginRedirectPath } from '@/lib/auth/session';
import { findKagemaneTemplate, kagemanePath, type KagemaneTemplate } from '@/lib/kagemane-catalog';
import { toast } from '@/lib/stores/toast-store';
import { getAuthSessionVersion, useAuthStore } from '@/stores/authStore';
import { trackFunnelEvent } from '@/lib/marketing/funnel';

const focusClass = 'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#4ABDE9] focus-visible:ring-offset-2';
const MAX_MATERIALS = 16;
type StudioView = 'references' | 'history';
const resolveStudioView = (tab: string | null): StudioView => tab === 'history' ? 'history' : 'references';
type AssignedMaterial = { material: UserMaterial; role: VideoReplicationReferenceRole };

function HistoryLoading() {
  const t = useTranslations('kagemane');
  return <GuideOrbLoading label={t('historyLoading')} />;
}

// 交互弹窗与工作台一起就绪；素材/视频的异步加载留在各自弹窗内部。
const KagemaneHistory = dynamic(() => import('./KagemaneHistory').then((module) => module.KagemaneHistory), { loading: HistoryLoading });

export function KagemaneStudio({
  onBack,
}: {
  onBack: () => void;
}) {
  const catalog = useKagemaneCatalog();
  useKagemanePerformance(catalog.loading, catalog.error);
  const t = useTranslations('kagemane');
  const exploreT = useTranslations('explore');
  const locale = useLocale();
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const ownerId = useAuthStore((state) => state.user?.id);
  const [selectedItem, setSelectedItem] = useState<KagemaneTemplate | null>(null);
  const [detailItem, setDetailItem] = useState<KagemaneTemplate | null>(null);
  const reducedMotion = useReducedMotion();
  const setupPanelRef = useRef<HTMLElement>(null);
  const referencePanelRef = useRef<HTMLElement>(null);
  const [historyActionsContainer, setHistoryActionsContainer] = useState<HTMLDivElement | null>(null);
  const [view, setView] = useState<StudioView>('references');
  const [historyPreviewOpen, setHistoryPreviewOpen] = useState(false);
  const [submittedTask, setSubmittedTask] = useState<ProjectTask | null>(null);
  const session = useRef<KagemaneSession>({});
  const [customReference, setCustomReference] = useState<UserMaterial | null>(null);
  const [editingReference, setEditingReference] = useState<UserMaterial | null>(null);
  const [materials, setMaterials] = useState<AssignedMaterial[]>([]);
  const [model, setModel] = useState<'seedance' | 'heygen' | 'kling4' | 'genjutsu'>('kling4');
  const [genjutsuMode, setGenjutsuMode] = useState<GenjutsuMode>('motion-transfer');
  const [genjutsuPresetId, setGenjutsuPresetId] = useState('');
  const [genjutsuQuote, setGenjutsuQuote] = useState<GenjutsuQuote | null>(null);
  const [genjutsuQuoteRefresh, setGenjutsuQuoteRefresh] = useState(0);
  const [quoteRefresh, setQuoteRefresh] = useState(0);
  const [genjutsuCost, setGenjutsuCost] = useState<{ identity: string; quote: GenjutsuQuote | null; error: boolean } | null>(null);
  const [genjutsuDuration, setGenjutsuDuration] = useState<{ source: string; duration: number } | null>(null);
  const currentGenerationIdentity = useRef('');
  const [resolution, setResolution] = useState<KagemaneResolution>('720p');
  const [depthEnabled, setDepthEnabled] = useState(false);
  const [promptEnabled, setPromptEnabled] = useState(false);
  const [setupExpanded, setSetupExpanded] = useState(false);
  const [prompt, setPrompt] = useState('');
  const [picker, setPicker] = useState<'video' | 'image' | null>(null);
  const [opening, setOpening] = useState(false);
  const [useGift, setUseGift] = useState(false);
  const [giftReady, setGiftReady] = useState(false);
  const [giftStatus, setGiftStatus] = useState<'loading' | 'error' | 'ready' | 'unavailable'>('loading');
  const [giftRefreshKey, setGiftRefreshKey] = useState(0);
  const openingRef = useRef(false);
  const longReference = (customReference ? customReference.duration ?? 0 : selectedItem?.durationSeconds ?? 0) > 15;
  const effectiveResolution = resolution;
  const materialCount = materials.length;
  const sourceIdentity = JSON.stringify([ownerId, model, customReference ? { id: customReference.id, url: customReference.url } : { id: selectedItem?.id, version: selectedItem?.version }]);
  const sourceDuration = genjutsuDuration?.source === sourceIdentity ? genjutsuDuration.duration : customReference ? customReference.duration : selectedItem?.durationSeconds;
  const generationIdentity = JSON.stringify([sourceIdentity, model, genjutsuMode, effectiveResolution]);
  currentGenerationIdentity.current = generationIdentity;
  const genjutsuReady = Boolean(genjutsuQuote && genjutsuQuote.video_model === `higgsfield/genjutsu/${genjutsuMode}/v1.0`
    && genjutsuQuote.resolution === effectiveResolution && genjutsuQuote.billable_seconds === Math.ceil(sourceDuration ?? 0));
  const videoModel: VideoReplicationModel = model === 'genjutsu' ? `higgsfield/genjutsu/${genjutsuMode}/v1.0`
    : model === 'kling4' ? 'kling-v4-omni' : model === 'heygen' ? 'heygen-video-1' : longReference ? 'seedance-2.5' : 'seedance-2.0';
  const genericQuote = useKagemaneQuote({ ownerId, videoModel, resolution: effectiveResolution, duration: sourceDuration,
    enabled: useGift || model !== 'genjutsu', refreshKey: quoteRefresh, billingMode: useGift ? 'monthly_gift' : 'dobi' });
  const costIdentity = JSON.stringify([sourceIdentity, videoModel, effectiveResolution, sourceDuration, genjutsuQuoteRefresh]);
  const receiveGenjutsuCost = useCallback((snapshot: { quote: GenjutsuQuote | null; error: boolean }) => {
    setGenjutsuCost({ identity: costIdentity, ...snapshot });
  }, [costIdentity]);
  const currentGenjutsuCost = genjutsuCost?.identity === costIdentity ? genjutsuCost : null;
  const displayedQuote: KagemaneQuote | null = useGift || model !== 'genjutsu' ? genericQuote.quote
    : currentGenjutsuCost?.quote ? { ...currentGenjutsuCost.quote, pricing_mode: 'fixed',
      estimate_min: currentGenjutsuCost.quote.credits, estimate_max: currentGenjutsuCost.quote.credits } : null;
  const costStatus = useGift || model !== 'genjutsu' ? genericQuote.status
    : currentGenjutsuCost?.error ? 'error' : displayedQuote ? 'ready' : 'loading';
  const costReady = useGift ? (model !== 'genjutsu' || genjutsuReady) && giftReady && genericQuote.status === 'ready' && displayedQuote?.gift_applied === true
    : model === 'genjutsu' ? genjutsuReady && displayedQuote !== null : genericQuote.status === 'ready';
  const restyle = model === 'genjutsu' && genjutsuMode === 'restyle';
  const materialsRequired = !restyle;
  const maxMaterials = model === 'genjutsu' ? restyle ? 5 : 8 : model === 'kling4' ? 10 : model === 'heygen' ? 9 : MAX_MATERIALS;
  const modelError = model === 'genjutsu'
    ? (customReference || selectedItem) && (typeof sourceDuration !== 'number' || !Number.isFinite(sourceDuration) || sourceDuration < 4 || sourceDuration > 30) ? t('genjutsuDurationError')
      : materialCount > maxMaterials ? t('genjutsuMaterialsError', { count: maxMaterials, remove: materialCount - maxMaterials })
        : genjutsuMode === 'object-swap' && customReference?.width && customReference?.height && customReference.width * customReference.height < 409600 ? t('genjutsuPixelsError') : undefined
    : model === 'kling4' ? (customReference || selectedItem) && (typeof sourceDuration !== 'number' || !Number.isFinite(sourceDuration) || sourceDuration < 4 || sourceDuration > 30) ? t('genjutsuDurationError')
      : materialCount > 10 ? t('genjutsuMaterialsError', { count: 10, remove: materialCount - 10 }) : undefined
    : model === 'heygen' ? longReference ? t('heygenDurationError') : materialCount > 9 ? t('heygenMaterialsError') : undefined : undefined;
  const changeModel = (next: 'seedance' | 'heygen' | 'kling4' | 'genjutsu') => {
    setModel(next);
    setGenjutsuQuote(null);
    setGenjutsuDuration(null);
    if (next === 'kling4') { if (!['720p', '1080p', '2k', '4k'].includes(resolution)) setResolution('720p'); setGiftReady(false); }
    if (next === 'genjutsu') { setResolution('720p'); setGiftReady(false); }
    if (next === 'heygen' && !KAGEMANE_HEYGEN_RESOLUTIONS.includes(resolution)) setResolution('480p');
    if (next === 'seedance' && resolution === '768p') setResolution('720p');
  };
  const hasSelectedTemplate = catalog.items.some((item) => item.id === selectedItem?.id);
  const referenceName = customReference?.display_name || customReference?.name || (hasSelectedTemplate && selectedItem ? selectedItem.title || t(`templates.${selectedItem.slug}`) : t('noReference'));
  const promptLimit = model === 'genjutsu' ? 10000 : 6000;
  const promptTooLong = promptEnabled && prompt.length > promptLimit;

  useEffect(() => {
    setUseGift(false);
    setGiftReady(false);
    setGenjutsuQuote(null);
    setGenjutsuDuration(null);
    setGenjutsuPresetId('');
    setCustomReference(null);
    setEditingReference(null);
    setMaterials([]);
    setPrompt('');
    setPromptEnabled(false);
    setDepthEnabled(false);
    setSubmittedTask(null);
    setPicker(null);
    setOpening(false);
    openingRef.current = false;
    session.current = {};
  }, [ownerId]);

  useEffect(() => { setGenjutsuDuration(null); }, [sourceIdentity]);

  useEffect(() => {
    const restore = () => {
      const url = new URL(window.location.href);
      const item = findKagemaneTemplate(url.searchParams.get('template'));
      setSelectedItem(item ?? null);
      setView(resolveStudioView(url.searchParams.get('tab')));
    };
    restore();
    window.addEventListener('popstate', restore);
    return () => window.removeEventListener('popstate', restore);
  }, [catalog.items]);

  useEffect(() => {
    if (!isAuthenticated || catalog.loading || catalog.error) return;
    const url = new URL(window.location.href);
    const template = findKagemaneTemplate(url.searchParams.get('template'));
    const draft = consumeKagemaneLoginDraft(kagemaneDraftStorage('session'), template?.id ?? null, url.searchParams.get('project'), Date.now(), kagemaneDraftStorage('local'), useAuthStore.getState().user?.id);
    if (draft) {
      setPrompt(draft.prompt);
      setPromptEnabled(draft.promptEnabled);
      setDepthEnabled(draft.depthEnabled === true);
      setResolution(draft.resolution);
      // Older login drafts predate model selection and were authored for Seedance.
      setModel(draft.model ?? 'seedance');
      setGenjutsuMode(draft.genjutsuMode ?? 'motion-transfer');
      setGenjutsuPresetId(draft.genjutsuPresetId ?? '');
      if (draft.picker) setPicker(draft.picker);
    }
    try {
      const storage = kagemaneDraftStorage('session');
      const pending = storage?.getItem('kagemane:reopen-picker');
      if (pending === 'image' || pending === 'video') { storage?.removeItem('kagemane:reopen-picker'); setPicker(pending); }
    } catch { /* Legacy drafts may use a WebView storage surface that was revoked. */ }
  }, [isAuthenticated, catalog.items, catalog.loading, catalog.error]);

  const selectTemplate = (item: KagemaneTemplate) => {
    if (openingRef.current || session.current.pendingRequest?.dispatched) return;
    setSelectedItem(item);
    setCustomReference(null);
    const url = new URL(window.location.href);
    url.searchParams.set('template', item.id);
    window.history.replaceState(window.history.state, '', `${url.pathname}${url.search}${url.hash}`);
  };

  const changeView = (next: StudioView) => {
    setView(next);
    const url = new URL(window.location.href);
    if (next !== 'references') url.searchParams.set('tab', next);
    else url.searchParams.delete('tab');
    window.history.replaceState(window.history.state, '', `${url.pathname}${url.search}${url.hash}`);
  };

  const redirectToLogin = () => {
    const projectId = new URLSearchParams(window.location.search).get('project');
    saveKagemaneLoginDraft(kagemaneDraftStorage('session'), {
      templateId: selectedItem?.id ?? null, projectId, prompt, promptEnabled, depthEnabled, resolution, model, genjutsuMode,
      ...(genjutsuPresetId ? { genjutsuPresetId } : {}), picker: picker ?? undefined, ownerId: useAuthStore.getState().user?.id ?? null,
    }, Date.now(), kagemaneDraftStorage('local'));
    const [pathname, query] = kagemanePath(selectedItem?.id, projectId ?? undefined).split('?');
    const params = new URLSearchParams(query);
    if (locale.startsWith('zh')) params.set('lang', 'zh');
    if (view !== 'references') params.set('tab', view);
    requestLogin(buildLoginRedirectPath(pathname, `?${params}`));
  };

  const openPicker = (next: 'video' | 'image') => {
    if (openingRef.current || session.current.pendingRequest?.dispatched) return;
    setPicker(next);
  };

  const refreshGifts = useCallback(() => setGiftRefreshKey(value => value + 1), []);
  const submissionUnresolved = Boolean(session.current.pendingRequest?.dispatched);

  const handleUse = async () => {
    if (openingRef.current || promptTooLong || modelError || (isAuthenticated && !costReady && !submissionUnresolved) || (!customReference && (catalog.loading || catalog.error || !hasSelectedTemplate))) return;
    if (materialsRequired && materialCount === 0) {
      toast.warning(t('requiredMaterials'));
      return;
    }
    const projectId = new URLSearchParams(window.location.search).get('project');
    if (!useAuthStore.getState().isAuthenticated) {
      redirectToLogin();
      return;
    }

    const owner = useAuthStore.getState().user?.id;
    const authVersion = getAuthSessionVersion();
    const submittingSession = session.current;
    const isCurrentOwner = () => session.current === submittingSession
      && getAuthSessionVersion() === authVersion && useAuthStore.getState().isAuthenticated && useAuthStore.getState().user?.id === owner;
    const assertOwner = () => { if (!isCurrentOwner()) throw new Error(t('openFailed')); };
    openingRef.current = true;
    setOpening(true);
    trackFunnelEvent('generation_attempted');
    try {
      submittingSession.projectId ??= projectId ?? undefined;
      const result = await submitKagemaneGeneration({
        session: submittingSession, assertOwner, template: selectedItem, customReference, materials,
        model, genjutsuMode, genjutsuPresetId, genjutsuMaxCredits: model === 'genjutsu'
          ? submissionUnresolved ? submittingSession.pendingRequest?.genjutsuMaxCredits : genjutsuQuote?.credits : undefined,
        depthEnabled: model !== 'genjutsu' && depthEnabled, resolution: effectiveResolution, prompt: promptEnabled ? prompt.trim() : '',
        useMembershipGift: useGift,
        giftMaxCredits: useGift ? submissionUnresolved ? submittingSession.pendingRequest?.giftMaxCredits
          : displayedQuote?.credits ?? undefined : undefined,
        name: referenceName, origin: window.location.origin, errorMessage: t('openFailed'),
      });
      if (!isCurrentOwner()) return;
      trackFunnelEvent('generation_submitted', result.task_id);
      setSubmittedTask({
        id: result.task_id, project_id: result.project_id, task_type: result.task_type ?? (model === 'genjutsu' ? 'genjutsu_video' : 'seedance_multimodal_video'),
        status: result.status, progress: 0, created_at: new Date().toISOString(),
        input_params: { resolution: result.resolution, video_model: result.video_model,
          ...(model === 'genjutsu' ? { genjutsu_mode: genjutsuMode, ...(restyle ? { genjutsu_preset_id: genjutsuPresetId } : {}) } : {}),
          ...(!customReference ? { source_catalog_id: selectedItem?.id } : {}) },
      });
      changeView('history');
      toast.success(t('generationSubmitted'));
    } catch (error) {
      const changed = error as { code?: string; details?: { actual_duration_sec?: unknown; video_model?: unknown; resolution?: unknown } };
      if (isCurrentOwner() && changed.code === 'KAGEMANE_GENJUTSU_QUOTE_CHANGED'
        && currentGenerationIdentity.current === generationIdentity && model === 'genjutsu'
        && changed.details?.video_model === `higgsfield/genjutsu/${genjutsuMode}/v1.0`
        && changed.details.resolution === effectiveResolution
        && typeof changed.details.actual_duration_sec === 'number' && Number.isFinite(changed.details.actual_duration_sec)
        && changed.details.actual_duration_sec >= 4 && changed.details.actual_duration_sec <= 30) {
        setGenjutsuQuote(null);
        setGenjutsuDuration({ source: sourceIdentity, duration: changed.details.actual_duration_sec });
      }
      if (isCurrentOwner()) toast.showError(error, 'generation', { fallbackMessage: t('openFailed') });
    } finally {
      if (isCurrentOwner()) {
        setGiftRefreshKey(value => value + 1);
        if (!submittingSession.pendingRequest?.dispatched) {
          setQuoteRefresh(value => value + 1);
          setGenjutsuQuoteRefresh(value => value + 1);
        }
        openingRef.current = false;
        setOpening(false);
      }
    }
  };


  const revealMobilePanel = (panel: HTMLElement | null) => {
    if (window.matchMedia('(min-width: 1024px)').matches) return;
    panel?.scrollIntoView({ block: 'start', behavior: reducedMotion ? 'auto' : 'smooth' });
  };

  useEffect(() => {
    if (view === 'history' && !window.matchMedia('(min-width: 1024px)').matches) {
      referencePanelRef.current?.scrollIntoView({ block: 'start', behavior: reducedMotion ? 'auto' : 'smooth' });
    }
  }, [view, submittedTask?.id, reducedMotion]);

  const costSnapshot = { authenticated: isAuthenticated,
    hasSource: Boolean(customReference || hasSelectedTemplate) && typeof sourceDuration === 'number' && Number.isFinite(sourceDuration) && sourceDuration >= 4 && sourceDuration <= 30,
    quote: displayedQuote, status: costStatus, useGift, giftReady, giftStatus };
  const generationLabel = useKagemaneGenerationLabel(costSnapshot);
  const showCostIcon = isAuthenticated && costSnapshot.hasSource && costReady;
  const generateButton = (
    <div>
    <KagemaneGenerationCost {...costSnapshot} disabled={opening || submissionUnresolved}
      onRetry={() => { setQuoteRefresh(value => value + 1); setGenjutsuQuoteRefresh(value => value + 1); setGiftRefreshKey(value => value + 1); }} />
    <StudioAction type="button" onClick={() => void handleUse()} disabled={opening || (materialsRequired && materialCount === 0) || promptTooLong || Boolean(modelError) || (isAuthenticated && !costReady && !submissionUnresolved) || (!customReference && (catalog.loading || catalog.error || !hasSelectedTemplate))}
      aria-label={opening ? t('submitting') : showCostIcon ? t('costGenerateAria', { cost: generationLabel }) : generationLabel}
      title={materialsRequired && materialCount === 0 ? t('requiredMaterials') : undefined}
      className={`${studioStyles.generate} w-full gap-2 text-base leading-6 ${focusClass}`}>
      {!opening && showCostIcon && <Coins className="size-4 shrink-0" aria-hidden="true" />}
      <span className="block leading-6" aria-live="polite" aria-atomic="true">{opening ? t('submitting') : generationLabel}</span>
    </StudioAction>
    </div>
  );

  const studioHeading = <div className="flex min-w-0 items-center gap-1">
    <button type="button" onClick={onBack} aria-label={exploreT('kagemaneBackToExplore')} title={exploreT('kagemaneBackToExplore')}
      className={`grid size-9 shrink-0 place-items-center rounded-lg text-[#667680] transition-colors hover:bg-[#F2F6F8] hover:text-[#1B242B] ${focusClass}`}>
      <ArrowLeft className="size-4" aria-hidden />
    </button>
    <span className="truncate text-sm font-semibold leading-5">{t('title')}</span>
  </div>;
  const referenceNavigation = <div className="inline-flex items-center gap-1" role="group" aria-label={t('studioNavigation')}>
    {(['references', 'history'] as const).map((tab) => <button key={tab} type="button" aria-pressed={view === tab} onClick={() => changeView(tab)}
      className={`flex min-h-9 items-center whitespace-nowrap rounded-lg border px-3 text-sm font-medium leading-5 transition-colors ${view === tab ? 'border-[#DCE5EB] bg-[#F0F5F8] text-[#176B89]' : 'border-transparent text-[#667680] hover:bg-[#F4F7F9] hover:text-[#1B242B]'} ${focusClass}`}>
      {t(tab === 'references' ? 'studioChooseReference' : tab)}
    </button>)}
  </div>;

  return (
    <div className={`${studioStyles.studio} relative flex min-h-0 flex-1 flex-col bg-white text-[#1B242B]`}>
      <h1 id="kagemane-title" className="sr-only">{t('title')}</h1>
      <div className={`${studioStyles.frame} flex min-h-0 flex-1 flex-col p-3 lg:p-4`} inert={picker || detailItem ? true : undefined} aria-hidden={picker || detailItem ? true : undefined}>
        <header className="mb-2 flex shrink-0 items-center justify-between gap-2 lg:hidden">
          {studioHeading}
        </header>
        <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain lg:overflow-hidden">
          <div className={`${studioStyles.panels} grid min-h-0 grid-cols-1 gap-3 lg:h-full`}>
            <aside ref={setupPanelRef} id="kagemane-setup-panel" aria-label={t('studioSetup')} className={`${studioStyles.panel} flex min-h-0 min-w-0 flex-col overflow-hidden rounded-[20px] border border-[#DCE5EB] bg-white outline-none`}>
              <header className={`${studioStyles.setupHeader} flex min-h-11 shrink-0 items-center lg:min-h-14 lg:px-3`}>
                <KagemaneMobileSetup referenceName={referenceName}
                  hasReference={Boolean(customReference || hasSelectedTemplate)}
                  referencePoster={customReference ? customReference.thumbnail_url : hasSelectedTemplate ? selectedItem?.previewPoster || selectedItem?.poster : undefined}
                  materials={materials.map(({ material }) => material)} optionalPeople={restyle}
                  modelLabel={t(model === 'kling4' ? 'modelKling4' : model === 'seedance' ? 'modelSeedance' : model === 'heygen' ? 'modelHeygen' : 'modelGenjutsu')}
                  resolution={effectiveResolution} expanded={setupExpanded} disabled={opening || submissionUnresolved}
                  validationMessage={modelError || (promptTooLong ? t('promptTooLong', { limit: promptLimit }) : undefined)}
                  settingsHint={restyle && !genjutsuPresetId ? t('genjutsuSelectStyle') : undefined}
                  onToggleSettings={() => setSetupExpanded(value => !value)}
                  onChangeReference={() => openPicker('video')} onAddMaterials={() => openPicker('image')}
                  onEditReference={customReference ? () => setEditingReference(customReference) : undefined} />
                <div className="hidden lg:block">{studioHeading}</div>
              </header>
              <div id="kagemane-setup-fields" className={`${setupExpanded ? 'block' : 'hidden'} p-3 pt-0 lg:block lg:min-h-0 lg:flex-1 lg:overflow-y-auto lg:overscroll-contain`}>
                <KagemaneSetupFields compactMobile
                  referenceSrc={customReference ? customReference.url : hasSelectedTemplate && selectedItem && !catalog.loading ? selectedItem.previewSrc || selectedItem.src : undefined}
                  onEditReference={customReference ? () => setEditingReference(customReference) : undefined}
                  referenceName={referenceName} referencePoster={customReference ? customReference.thumbnail_url : hasSelectedTemplate && selectedItem ? selectedItem.previewPoster || selectedItem.poster : undefined}
                  materials={materials.map(({ material }) => material)} maxMaterials={maxMaterials} disabled={opening || submissionUnresolved} previewActive={!picker && !detailItem && !editingReference && !historyPreviewOpen}
                  onChangeReference={() => openPicker('video')} onAddMaterial={() => openPicker('image')}
                  onRemoveMaterial={(id) => setMaterials((current) => current.filter((item) => item.material.id !== id))}
                  depthEnabled={depthEnabled} showDepth={model !== 'genjutsu'} onToggleDepth={() => setDepthEnabled(current => !current)}
                  promptEnabled={promptEnabled} onTogglePrompt={() => setPromptEnabled((current) => !current)}
                  prompt={prompt} onPromptChange={setPrompt} promptLimit={promptLimit} promptTooLong={promptTooLong}
                  resolution={effectiveResolution} resolutionOptions={model === 'genjutsu' ? ['480p', '720p', '1080p'] : model === 'kling4' ? ['720p', '1080p', '2k', '4k'] : model === 'heygen' ? KAGEMANE_HEYGEN_RESOLUTIONS : longReference ? KAGEMANE_RESOLUTIONS_25 : undefined}
                  onResolutionChange={value => { setResolution(value); setGenjutsuQuote(null); }} model={model} onModelChange={changeModel} modelError={modelError}
                  optionalPeople={restyle}
                  genjutsuFields={model === 'genjutsu' ? <KagemaneGenjutsuFields key={ownerId ?? 'guest'} mode={genjutsuMode}
                    onModeChange={value => { setGenjutsuMode(value); setGenjutsuQuote(null); }} presetId={genjutsuPresetId}
                    onPresetChange={value => { setGenjutsuPresetId(value); setGenjutsuQuote(null); }}
                    resolution={effectiveResolution} duration={sourceDuration} ownerId={ownerId} disabled={opening || submissionUnresolved}
                    onQuoteChange={setGenjutsuQuote} refreshKey={genjutsuQuoteRefresh} onCostChange={receiveGenjutsuCost} showQuote={false} /> : undefined}
                />
                {submissionUnresolved && <p role="status" className="mt-2 text-xs text-[#667680]">{t('submissionUnknown')}</p>}
                {isAuthenticated && ownerId && <div className="mt-2.5"><KagemaneGiftChoice key={ownerId}
                  ownerId={ownerId} selected={useGift} disabled={opening || submissionUnresolved}
                  duration={customReference ? customReference.duration : hasSelectedTemplate ? selectedItem?.durationSeconds : undefined}
                  model={model} videoModel={videoModel} resolution={effectiveResolution} refreshKey={giftRefreshKey}
                  onAvailabilityChange={setGiftReady} onStatusChange={setGiftStatus} onChange={(selected) => {
                    setUseGift(selected);
                    if (selected) setGiftStatus('loading');
                    // A coupon never changes the user's model or resolution.
                  }}
                /></div>}

              </div>
              <div className="hidden shrink-0 border-t border-[#E8EEF2] bg-white px-3 pb-3 pt-2 lg:block">{generateButton}</div>
            </aside>

            <section ref={referencePanelRef} id="kagemane-reference-panel" aria-label={t(view === 'references' ? 'studioLibrary' : view)} className={`${studioStyles.panel} flex min-h-0 min-w-0 flex-col overflow-hidden rounded-[20px] border border-[#DCE5EB] bg-white outline-none`}>
              <header className="flex min-h-14 shrink-0 items-center gap-2 overflow-x-auto px-3 lg:px-4" data-kagemane-history-header>
                <div className="shrink-0">{referenceNavigation}</div>
                <div ref={setHistoryActionsContainer} className="ml-auto shrink-0" data-kagemane-history-actions-slot />
              </header>
              <div className="p-3 lg:min-h-0 lg:flex-1 lg:overflow-y-auto lg:overscroll-contain lg:px-5 lg:pb-5 lg:pt-0">
              <KagemaneCatalogNotice error={catalog.error} retry={catalog.retry} />
              {catalog.loading && view !== 'history' && <KagemaneMasonry columnCount={KAGEMANE_LIBRARY_COLUMNS} role="status" aria-label={t('catalogLoading')}>
                {Array.from({ length: 6 }, (_, index) => <div key={index} aria-hidden="true" className="aspect-[9/16] rounded-[16px] bg-[#ECEFF2]" />)}
              </KagemaneMasonry>}
              {!catalog.loading && !catalog.error && catalog.items.length === 0 && view !== 'history' && <p role="status" className="py-8 text-center text-sm text-hr-text-secondary">{t('catalogEmpty')}</p>}
              {view === 'history' ? <KagemaneHistory key={submittedTask?.id ?? 'history'} submittedTask={submittedTask} onPreviewOpenChange={setHistoryPreviewOpen} onGiftsChanged={refreshGifts} onRequireLogin={redirectToLogin} actionsContainer={historyActionsContainer} /> :
              <KagemaneMasonry columnCount={KAGEMANE_LIBRARY_COLUMNS} id="kagemane-templates" role="list" aria-label={t('studioLibrary')}>
                {(catalog.loading ? [] : catalog.items).map((item) => <KagemaneReferenceCard
                  key={`${item.id}:${item.previewSrc ?? ''}:${item.previewPoster || item.poster}`}
                  item={item} name={item.title || t(`templates.${item.slug}`)}
                  selected={!customReference && item.id === selectedItem?.id} disabled={opening || submissionUnresolved}
                  playbackEnabled={!picker && !detailItem && !editingReference && !historyPreviewOpen && !opening && !submissionUnresolved}
                  onSelect={() => { if (openingRef.current || session.current.pendingRequest?.dispatched) return; setDetailItem(item); }}
                />)}
              </KagemaneMasonry>}
              </div>
            </section>
          </div>
        </div>
        <div className={`${studioStyles.mobileAction} shrink-0 border-t border-[#E8EEF2] bg-white pt-3 lg:hidden`}>{generateButton}</div>
      </div>
      {detailItem && <KagemaneVideoDetail studioChrome key={detailItem.id} item={detailItem} onClose={() => setDetailItem(null)} onRecreate={() => { if (openingRef.current || session.current.pendingRequest?.dispatched) return; selectTemplate(detailItem); setDetailItem(null); revealMobilePanel(setupPanelRef.current); if (materialsRequired && materials.length === 0) openPicker('image'); }} />}
      {editingReference && <KagemaneReferenceEditor source={{ material: editingReference }} maxDurationMs={model === 'heygen' ? 15000 : 30000} onClose={() => setEditingReference(null)} onConfirm={material => { setCustomReference(material); setEditingReference(null); }} />}
      {picker && <KagemaneMediaPicker maxVideoDurationMs={model === 'heygen' ? 15000 : 30000} kind={picker} authenticated={isAuthenticated} onRequireLogin={redirectToLogin} onClose={() => setPicker(null)} onSelect={(material) => { if (openingRef.current || session.current.pendingRequest?.dispatched) return; setCustomReference(material); setPicker(null); }}
        assignments={Object.fromEntries(materials.map(({ material, role }) => [material.id, role]))}
        maxAssignments={maxMaterials}
        onAssign={(material, role) => setMaterials((current) => {
          if (openingRef.current || session.current.pendingRequest?.dispatched) return current;
          const existing = current.findIndex((item) => item.material.id === material.id);
          if (existing >= 0) return current.map((item, index) => index === existing ? { material, role } : item);
          return current.length < maxMaterials ? [...current, { material, role }] : current;
        })}
        onRemove={(id) => setMaterials((current) => current.filter((item) => item.material.id !== id))} />}
    </div>
  );
}
